import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../utils/api';
import { memberPortalBranding } from '../../config/memberPortalBranding';

export default function MemberRegister() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gender: '',
    date_of_birth: '',
    constituency: '',
    student_id_number: '', // School/Student ID (required)
    school_completion_date: '', // Expected month & year of completion (required for lifecycle)
  });
  const [constituencies, setConstituencies] = useState([]);
  const [loadingConstituencies, setLoadingConstituencies] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [associationNumber, setAssociationNumber] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const landingBackground =
    'radial-gradient(circle at 12% 18%, rgba(0,107,63,0.26), transparent 40%), radial-gradient(circle at 90% 86%, rgba(196,30,58,0.28), transparent 36%), linear-gradient(135deg, #f7f9f8 0%, #e9f3ee 20%, #b8d8c8 40%, #0b5e35 62%, #7a1f28 82%, #151515 100%)';

  useEffect(() => {
    fetchConstituencies();
  }, []);

  const fetchConstituencies = async () => {
    try {
      setLoadingConstituencies(true);
      // Use a separate API instance or modify to not require auth for this endpoint
      const response = await api.get('/member-portal/constituencies');
      setConstituencies(response.data.data || []);
    } catch (err) {
      console.error('Error fetching constituencies:', err);
      // Fallback: use empty array, form will still work
      setConstituencies([]);
    } finally {
      setLoadingConstituencies(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/i)) {
      setError('Please select a JPEG, PNG, or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    setError('');
    setProfilePicture(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicturePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!profilePicture) {
      setError('Profile photo is required');
      setLoading(false);
      return;
    }

    try {
      const payload = new FormData();
      payload.append('profile_picture', profilePicture);
      payload.append('first_name', formData.first_name);
      payload.append('last_name', formData.last_name);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      payload.append('password', formData.password);
      payload.append('gender', formData.gender);
      if (formData.date_of_birth) payload.append('date_of_birth', formData.date_of_birth);
      if (formData.constituency) payload.append('constituency', formData.constituency);
      if (formData.student_id_number?.trim()) payload.append('student_id_number', formData.student_id_number.trim());
      if (formData.school_completion_date) payload.append('school_completion_date', formData.school_completion_date);

      const response = await api.post('/member-portal/register', payload);

      if (response.data.success) {
        const memberData = response.data.data?.member;
        const assocNumber = memberData?.association_number || memberData?.member_id;
        if (assocNumber) {
          setAssociationNumber(assocNumber);
        }
        
        const message = response.data.data?.is_new_member
          ? `Registration successful! Your Association Number is: ${assocNumber || 'Pending'}. Your account is pending approval. You will be notified once approved.`
          : `Login credentials added successfully! Your Association Number is: ${assocNumber || 'Pending'}. Your account is pending approval. You will be notified once approved.`;
        setSuccess(response.data.message || message);
        // Redirect to login after 5 seconds (give time to note the association number)
        setTimeout(() => {
          navigate('/member-portal/login');
        }, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: landingBackground,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <GroupsIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
              Join {memberPortalBranding.associationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your account to access the member portal
            </Typography>
          </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
            {associationNumber && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Your Association Number:
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, fontFamily: 'monospace' }}>
                  {associationNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Please save this number for your records
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Profile photo <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={profilePicturePreview || undefined}
                  sx={{ width: 96, height: 96, bgcolor: 'primary.main', fontSize: '2rem' }}
                >
                  {(formData.first_name?.[0] || '') + (formData.last_name?.[0] || '') || '?'}
                </Avatar>
                <Box>
                  <input
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    style={{ display: 'none' }}
                    id="registration-profile-picture"
                    type="file"
                    onChange={handlePictureChange}
                  />
                  <label htmlFor="registration-profile-picture">
                    <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                      {profilePicture ? 'Change photo' : 'Choose photo'}
                    </Button>
                  </label>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    JPEG, PNG, or GIF — max 5MB (required)
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                autoComplete="tel"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  label="Gender"
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School / Student ID"
                name="student_id_number"
                value={formData.student_id_number}
                onChange={handleChange}
                required
                helperText="Your institution student or school ID"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected month & year of completing school"
                name="school_completion_date"
                type="month"
                value={formData.school_completion_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                helperText="When this date is reached, your status will automatically become Alumni"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Constituency</InputLabel>
                <Select
                  name="constituency"
                  value={formData.constituency}
                  onChange={handleChange}
                  label="Constituency"
                  disabled={loadingConstituencies}
                >
                  <MenuItem value="">
                    <em>Select Constituency</em>
                  </MenuItem>
                  {constituencies.map((constituency) => (
                    <MenuItem key={constituency.id || constituency.name} value={constituency.name}>
                      {constituency.name}
                    </MenuItem>
                  ))}
                </Select>
                {loadingConstituencies && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Loading constituencies...
                    </Typography>
                  </Box>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                helperText="Minimum 6 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Register'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link to="/member-portal/login" style={{ textDecoration: 'none' }}>
              <Button variant="text" size="small">
                Login here
              </Button>
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
    </Box>
  );
}
